/* eslint-disable @typescript-eslint/no-explicit-any */
import dayjs from 'dayjs';
import Cookie from 'js-cookie';
import qs from 'query-string';

import {
  TOKEN,
  REFRESH_TOKEN,
  LAST_REFRESH_TIME,
  TOKEN_VALID_DAY,
  USER_EMAIL,
  EDU_URL,
  USER_ID,
  SCH_URL,
} from '@/constants';

import CacheStorage from './cache-storage';
import { goWebSite } from './go-web-site';

/**
 * 将base64字符串转换为json对象
 *
 * @param {string} params
 * @return {*}
 */
export const decodeAndParseParams = (params: string) => {
  try {
    if (!params) {
      return {};
    }
    return JSON.parse(decodeURIComponent(window.atob(params)));
  } catch (error) {
    console.error('Failed to parse params:', error);
    return {};
  }
};

/**
 * 判断是否为游客模式
 *
 * @return {boolean}
 */
export const isTourist = () => {
  const { params } = qs.parse(globalThis.location?.search || '');
  const { accessMode } = decodeAndParseParams(params as string);
  return accessMode === 'tourist';
};

export const mainDomainTokenSet = (tokenKey?: string) => {
  Cookie.set(tokenKey ?? TOKEN, CacheStorage.getItem(TOKEN), {
    domain: window.location.href.includes('edu.net') ? EDU_URL : SCH_URL,
    path: '/',
  });
};

export const setTokenInfo = (token: string, refreshToken: string) => {
  CacheStorage.setItem(TOKEN, token);
  CacheStorage.setItem(REFRESH_TOKEN, refreshToken);
  CacheStorage.setItem(
    LAST_REFRESH_TIME,
    `${dayjs().add(TOKEN_VALID_DAY, 'day').unix()}`,
  );
};

export const setRealToken = (token: string) => {
  CacheStorage.setItem(TOKEN, token);
  CacheStorage.setItem(
    LAST_REFRESH_TIME,
    `${dayjs().add(TOKEN_VALID_DAY, 'day').unix()}`,
  );
};

export const removeRealToken = () => {
  Cookie.remove(TOKEN);
  CacheStorage.removeItem(LAST_REFRESH_TIME);
};

export const removeTokenInfo = (params?: any, quite = true) => {
  Cookie.remove(TOKEN);
  CacheStorage.removeItem(REFRESH_TOKEN);
  CacheStorage.removeItem(LAST_REFRESH_TIME);
  CacheStorage.removeItem(USER_EMAIL);
  CacheStorage.removeItem(USER_ID);
  window?.Intercom?.('shutdown');
  try {
    if (quite) {
      goWebSite();
    }
    Cookie.remove(TOKEN, {
      domain: EDU_URL,
      path: '/',
    });
    Cookie.remove(TOKEN, {
      domain: SCH_URL,
      path: '/',
    });
  } catch (e) {
    console.log(e);
  }
};

export const checkTokenInfo = () => {
  const token = CacheStorage.getItem(TOKEN);

  return !!token && !isTourist();
};

/**
 * 检查用户是否已登录（仅判断 token 是否存在）
 * 用于移动端免登录场景的登录状态判断
 */
export const checkIsLoggedIn = (): boolean => {
  return !!CacheStorage.getItem(TOKEN);
};

export const generateNamePrefix = (prefix: string) => {
  return (string: any, ...params: any[]): string => {
    return [prefix, ...string, ...params].join('');
  };
};

/**
 * 存储本地数据
 * @param {String} type 存储类型sessionStorage\localStorage
 * @param {String} key 存储的key
 * @param { Object } value 存储的值
 */
export const setStorage = (
  type: 'sessionStorage' | 'localStorage' = 'localStorage',
  key: string,
  value: unknown,
) => {
  try {
    if (key) {
      const formatValue = JSON.stringify(value);
      if (!isServerSide()) {
        window[type].setItem(key, formatValue);
      }
    }
  } catch (error) {
    console.error(error);
  }
};

/**
 * 判断浏览器环境
 * @return { Boolean } 是否是服务端
 */
export const isServerSide = () => {
  return typeof window === 'undefined';
};

/**
 * 获取存储本地数据
 * @param {String} type 存储类型sessionStorage\localStorage
 * @param {String} key 存储的key
 * @return {any}
 */
export const getStorage = (
  type: 'sessionStorage' | 'localStorage' = 'localStorage',
  key: string,
): any => {
  try {
    if (!isServerSide()) {
      const value = window[type].getItem(key) || 'null';
      return JSON.parse(value);
    }
    return null;
  } catch (error: any) {
    return Error(error);
  }
};

/**
 * 删除存储本地数据
 * @param {String} type 存储类型sessionStorage\localStorage
 * @param {String} key 存储的key
 *
 */
export const delStorage = (
  type: 'sessionStorage' | 'localStorage' = 'localStorage',
  key: string,
) => {
  try {
    if (key) {
      if (!isServerSide()) {
        window[type].removeItem(key);
      }
    }
  } catch (error) {
    console.error(error);
  }
};

export const isNumber = (value: any) => {
  if (value === null) {
    return false;
  }
  return !isNaN(Number(value));
};

export const forbiddenBrowser = (): boolean => {
  const ua = navigator.userAgent.toLowerCase();
  return /msie/.test(ua) || /firefox/.test(ua) || /opera|opr/.test(ua);
};
